import type { NotificationData } from '../../services/notification.service';
import { isWayRedeemedNotificationRow } from './way-redeem-notification.util';
import { coerceUsStateCode, splitLicensePlateForApi } from './split-license-plate-for-api';
import { isVendorMembershipSource } from '../../components/notification-modal/components/vendor-active-subscription-modal/vendor-active-subscription-display.util';

/** PrimeNG toast key for Way LPR auto-redeem / auto gate-open success. */
export const LANE_AUTO_REDEEM_SUCCESS_TOAST_KEY = 'lane-auto-redeem-success';

const AUTO_REDEEM_TOAST_TYPES = new Set(['order-success', 'active-subscription']);

/** True when auto-redeem succeeded — show the lane success toast (Way and vendor). */
export function shouldShowLaneAutoRedeemToast(
  notification: NotificationData | null | undefined
): boolean {
  if (!notification || !notification.type || !AUTO_REDEEM_TOAST_TYPES.has(notification.type)) {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  if (!data || typeof data !== 'object') {
    return false;
  }
  return data['auto_redeem'] === true || data['gate_opened'] === true;
}

/**
 * True when auto-redeem should show toast only (skip auto-opening the LPR modal).
 * Way order-success / membership and local (in-shop/SaaS-bought) memberships stay
 * toast-only — vendor-sourced (DRB/SiteWatch/etc) auto-redeem still also opens the
 * Active Subscription modal, since attendants need to see vendor-specific details
 * (source, payment collection) that the toast doesn't carry.
 */
export function isLaneAutoRedeemSuccessNotification(
  notification: NotificationData | null | undefined
): boolean {
  if (!notification || !shouldShowLaneAutoRedeemToast(notification)) {
    return false;
  }
  const data = notification.data as Record<string, any> | undefined;
  if (notification.type === 'active-subscription' && data && !isWayRedeemedNotificationRow(data)) {
    // Mirrors vendor-active-subscription-display.util.ts's resolveSourceLabel fallback
    // chain: customer_source, then customers_source, then the bare source field —
    // any of them can carry the vendor tag depending on which redemption path produced
    // the row.
    const customerSource = String(data['customer_source'] ?? '').trim();
    const customersSource = String(data['customers_source'] ?? '').trim();
    const rawSource = String(data['source'] ?? '').trim();
    if (
      isVendorMembershipSource(customerSource) ||
      isVendorMembershipSource(customersSource) ||
      isVendorMembershipSource(rawSource)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * CWS-1053: true only when a gate is actually opening for this lane.
 *
 * The backend used to set `gate_opened` from `auto_redeem` alone, so a lane with no
 * automatic gate still claimed one was opening. It now reflects real lane wiring, and the
 * toast must read the flag rather than assume it — an absent flag means no gate.
 */
export function isLaneGateOpening(notification: NotificationData | null | undefined): boolean {
  const data = notification?.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') {
    return false;
  }
  return data['gate_opened'] === true;
}

export function buildLaneAutoRedeemToastSummary(notification: NotificationData): string {
  const data = notification.data as Record<string, any> | undefined;
  if (data?.['auto_rewash'] === true || data?.['is_rewash'] === true) {
    return 'Rewash started';
  }
  if (notification.type !== 'active-subscription') {
    return 'Way purchase redeemed';
  }
  if (data && !isWayRedeemedNotificationRow(data)) {
    return 'Membership redeemed';
  }
  return 'Way membership redeemed';
}

/** Prefer backend stripped_plate; otherwise strip state prefix from plate. */
export function resolveLaneAutoRedeemToastPlate(
  data: Record<string, any> | null | undefined
): string {
  const row = data ?? {};
  const stripped = String(row['stripped_plate'] ?? row['strippedPlate'] ?? '').trim();
  if (stripped) {
    return stripped.toUpperCase();
  }

  const raw = String(row['plate'] ?? row['vehicle_license_plate'] ?? '').trim();
  if (!raw) {
    return '';
  }
  const hintState = coerceUsStateCode(
    String(row['state'] ?? row['vehicle_state'] ?? '').trim() || null
  );
  const { license_plate } = splitLicensePlateForApi(raw, hintState);
  return (license_plate || raw).trim().toUpperCase();
}

export function buildLaneAutoRedeemToastDetail(notification: NotificationData): string {
  const data = (notification.data ?? {}) as Record<string, any>;
  const plate = resolveLaneAutoRedeemToastPlate(data);
  const laneName = String(data['lane_name'] ?? '').trim();
  const parts = [plate, laneName].filter(Boolean);
  if (parts.length === 0) {
    return 'Open details to review the order.';
  }
  return parts.join(' · ');
}
